
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { 
  Table, 
  TableBody, 
  TableCaption, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Pencil, Plus, Trash2, Upload } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";

interface AIAgent {
  id: string;
  name: string;
  photo_url: string | null;
  background: string;
  personality: string;
  expertise: string | null;
  is_default: boolean;
  elevenlabs_voice_id: string | null;
  created_at: string;
  updated_at: string;
}

interface AIAgentFormValues {
  name: string;
  photo_url: string;
  background: string;
  personality: string;
  expertise: string;
  is_default: boolean;
  elevenlabs_voice_id: string;
}

export function AIAgentManager() {
  const [agents, setAgents] = useState<AIAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentAgent, setCurrentAgent] = useState<AIAgent | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  
  const form = useForm<AIAgentFormValues>({
    defaultValues: {
      name: "",
      photo_url: "",
      background: "",
      personality: "",
      expertise: "",
      is_default: false,
      elevenlabs_voice_id: "",
    },
  });
  
  const fetchAgents = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('ai_agents')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) {
        throw error;
      }
      
      setAgents(data as AIAgent[]);
    } catch (err) {
      console.error('Error fetching AI agents:', err);
      setError('Failed to load AI agents');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchAgents();
  }, []);
  
  const handlePhotoUpload = async (file: File): Promise<string> => {
    if (!file) {
      return "";
    }
    
    try {
      setPhotoUploading(true);
      
      // Create a unique file name
      const fileExt = file.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      const filePath = `${fileName}`;
      
      // Upload file to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('ai_agent_photos')
        .upload(filePath, file);
        
      if (uploadError) {
        throw uploadError;
      }
      
      // Get public URL
      const { data } = supabase.storage
        .from('ai_agent_photos')
        .getPublicUrl(filePath);
      
      return data.publicUrl;
    } catch (err) {
      console.error('Error uploading photo:', err);
      toast({
        variant: "destructive",
        title: "Upload Failed",
        description: "Failed to upload agent photo",
      });
      return "";
    } finally {
      setPhotoUploading(false);
    }
  };
  
  const handleCreate = async (values: AIAgentFormValues) => {
    try {
      setLoading(true);
      
      // Upload photo if selected
      let photoUrl = values.photo_url;
      if (photoFile) {
        photoUrl = await handlePhotoUpload(photoFile);
        if (!photoUrl) {
          toast({
            variant: "destructive",
            title: "Error",
            description: "Failed to upload photo",
          });
          setLoading(false);
          return;
        }
      }
      
      // Clean up empty strings for nullable fields
      const agentData = {
        ...values,
        photo_url: photoUrl || null,
        expertise: values.expertise || null,
        elevenlabs_voice_id: values.elevenlabs_voice_id || null,
      };
      
      const { error } = await supabase
        .from('ai_agents')
        .insert([agentData]);
        
      if (error) {
        throw error;
      }
      
      toast({
        title: "Success",
        description: "AI Agent created successfully",
      });
      
      form.reset();
      setPhotoFile(null);
      fetchAgents();
    } catch (err) {
      console.error('Error creating AI agent:', err);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create AI agent",
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpdate = async (values: AIAgentFormValues) => {
    if (!currentAgent) return;
    
    try {
      setLoading(true);
      
      // Upload photo if selected
      let photoUrl = values.photo_url;
      if (photoFile) {
        photoUrl = await handlePhotoUpload(photoFile);
        if (!photoUrl) {
          toast({
            variant: "destructive",
            title: "Error",
            description: "Failed to upload photo",
          });
          setLoading(false);
          return;
        }
      }
      
      // Clean up empty strings for nullable fields
      const agentData = {
        ...values,
        photo_url: photoUrl || null,
        expertise: values.expertise || null,
        elevenlabs_voice_id: values.elevenlabs_voice_id || null,
      };
      
      const { error } = await supabase
        .from('ai_agents')
        .update(agentData)
        .eq('id', currentAgent.id);
        
      if (error) {
        throw error;
      }
      
      toast({
        title: "Success",
        description: "AI Agent updated successfully",
      });
      
      setIsEditing(false);
      setCurrentAgent(null);
      setPhotoFile(null);
      form.reset();
      fetchAgents();
    } catch (err) {
      console.error('Error updating AI agent:', err);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update AI agent",
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleSetDefault = async (id: string) => {
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('ai_agents')
        .update({ is_default: true })
        .eq('id', id);
        
      if (error) {
        throw error;
      }
      
      toast({
        title: "Success",
        description: "Default AI agent updated successfully",
      });
      
      fetchAgents();
    } catch (err) {
      console.error('Error setting default AI agent:', err);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to set default AI agent",
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this AI agent?")) return;
    
    try {
      setLoading(true);
      const { error } = await supabase
        .from('ai_agents')
        .delete()
        .eq('id', id);
        
      if (error) {
        throw error;
      }
      
      toast({
        title: "Success",
        description: "AI Agent deleted successfully",
      });
      
      fetchAgents();
    } catch (err) {
      console.error('Error deleting AI agent:', err);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete AI agent",
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleEdit = (agent: AIAgent) => {
    setCurrentAgent(agent);
    setIsEditing(true);
    form.reset({
      name: agent.name,
      photo_url: agent.photo_url || "",
      background: agent.background,
      personality: agent.personality,
      expertise: agent.expertise || "",
      is_default: agent.is_default,
      elevenlabs_voice_id: agent.elevenlabs_voice_id || "",
    });
  };
  
  const handleCancelEdit = () => {
    setIsEditing(false);
    setCurrentAgent(null);
    setPhotoFile(null);
    form.reset();
  };
  
  const onSubmit = (values: AIAgentFormValues) => {
    if (isEditing && currentAgent) {
      handleUpdate(values);
    } else {
      handleCreate(values);
    }
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setPhotoFile(e.target.files[0]);
    }
  };
  
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{isEditing ? "Edit AI Agent" : "Create New AI Agent"}</CardTitle>
          <CardDescription>
            {isEditing 
              ? "Edit the details of an existing AI agent" 
              : "Fill in the details to create a new AI agent for the system"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Agent name" {...field} />
                        </FormControl>
                        <FormDescription>
                          The name of the AI agent
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="expertise"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Expertise</FormLabel>
                        <FormControl>
                          <Input placeholder="Agent expertise" {...field} />
                        </FormControl>
                        <FormDescription>
                          The agent's area of expertise
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="photo_url"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Photo URL</FormLabel>
                        <div className="space-y-4">
                          <FormControl>
                            <Input 
                              placeholder="https://example.com/image.jpg (optional)" 
                              {...field} 
                              disabled={!!photoFile}
                            />
                          </FormControl>
                          
                          <div className="space-y-2">
                            <p className="text-sm text-muted-foreground">Or upload a photo</p>
                            <div className="flex items-center gap-4">
                              <Input
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                className="max-w-sm"
                              />
                              
                              {(field.value || photoFile) && (
                                <Avatar className="w-16 h-16">
                                  <AvatarImage 
                                    src={photoFile ? URL.createObjectURL(photoFile) : field.value} 
                                    alt="Preview" 
                                  />
                                  <AvatarFallback>AI</AvatarFallback>
                                </Avatar>
                              )}
                            </div>
                          </div>
                        </div>
                        <FormDescription>
                          URL or uploaded image for the agent's photo
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="elevenlabs_voice_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>ElevenLabs Voice ID (optional)</FormLabel>
                        <FormControl>
                          <Input placeholder="eleven_voice_id" {...field} />
                        </FormControl>
                        <FormDescription>
                          ElevenLabs voice ID for text-to-speech
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="is_default"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Set as Default Agent</FormLabel>
                          <FormDescription>
                            This agent will be used as the default when no specific agent is requested
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                </div>
                
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="background"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Background</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Agent's background and expertise" 
                            rows={5}
                            {...field} 
                          />
                        </FormControl>
                        <FormDescription>
                          The background and expertise of the AI agent
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="personality"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Personality</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Agent's personality traits" 
                            rows={5}
                            {...field} 
                          />
                        </FormControl>
                        <FormDescription>
                          Personality characteristics of the AI agent
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-2">
                {isEditing && (
                  <Button type="button" variant="outline" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                )}
                <Button 
                  type="submit" 
                  disabled={loading || photoUploading}
                  className="flex gap-2"
                >
                  {photoUploading && <div className="animate-spin rounded-full h-4 w-4 border-2 border-b-transparent border-white"></div>}
                  {isEditing ? "Update Agent" : "Create Agent"}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>AI Agents</CardTitle>
          <CardDescription>
            Manage existing AI agents in the system
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && !isEditing ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className="text-destructive text-center py-4">{error}</div>
          ) : (
            <Table>
              <TableCaption>List of AI agents in the system</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>Photo</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Expertise</TableHead>
                  <TableHead>Default</TableHead>
                  <TableHead>Voice ID</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {agents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center">No AI agents found</TableCell>
                  </TableRow>
                ) : (
                  agents.map((agent) => (
                    <TableRow key={agent.id} className={agent.is_default ? "bg-primary/5" : ""}>
                      <TableCell>
                        <Avatar>
                          <AvatarImage src={agent.photo_url || ''} alt={agent.name} />
                          <AvatarFallback>{agent.name.charAt(0)}</AvatarFallback>
                        </Avatar>
                      </TableCell>
                      <TableCell className="font-medium">{agent.name}</TableCell>
                      <TableCell>{agent.expertise || 'None'}</TableCell>
                      <TableCell>
                        {agent.is_default ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Default
                          </span>
                        ) : (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleSetDefault(agent.id)}
                            className="text-xs"
                          >
                            Set Default
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>{agent.elevenlabs_voice_id || 'None'}</TableCell>
                      <TableCell>
                        {new Date(agent.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end space-x-2">
                          <Button 
                            variant="outline" 
                            size="icon"
                            onClick={() => handleEdit(agent)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="icon" 
                            className="text-destructive" 
                            onClick={() => handleDelete(agent.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
